import { Box, Button, Flex, Image, Text } from '@chakra-ui/react';
import { useLocation, useNavigate } from 'react-router';

import type { MergedPricingService } from 'screens/Prices/utils/mergePricingService';
import { trackSelectItem } from 'utils/tracking';
import { getBrandColor } from 'utils/brandColor';
import { AlmaMention } from './AlmaMention';

/** Card border-radius from WP plugin settings (Personalisation > Arrondi des cards). */
const cardBorderRadius = `${Number(mondayMindbody?.customization?.cardBorderRadius) || 12}px`;

/** Brand primary color from WP plugin settings — default accent for ribbon + CTA. */
const brandColor = getBrandColor();

interface TopFeatureCardProps {
  merged: MergedPricingService;
  /** Mention Alma des cartes (pricingConfig.globalTexts.almaCardText). */
  almaText: string;
}

/**
 * Visual card for one top "feature" block.
 *
 * Two rendering modes:
 *
 * **Solid-color mode** (`bgColor` set): full colored background, centered
 * white text, white CTA button.
 *
 * **Maquette mode** (no `bgColor`): light-gray background (#f5f5f5),
 * left-aligned text, optional full-width ribbon bar at top, bottom row
 * with CTA (left) and validity/limit (right, italic). The card stretches
 * to fill its grid cell — height is driven by CSS grid `align-items:
 * stretch` set by the parent `TopBlocksRow`.
 */
export function TopFeatureCard({ merged, almaText }: TopFeatureCardProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const onCheckout = merged.service
    ? () => {
        const product = { ...merged.service!, type: 'service' as const, IsIntroOffer: !!merged.service!.IsIntroOffer };
        trackSelectItem(product as unknown as Record<string, unknown>);
        navigate(`/checkout/id/${product.Id}`, { state: { product, from: location } });
      }
    : undefined;

  const hasBg = !!merged.bgColor;
  const accentColor = merged.ctaColor || merged.ribbon?.color || brandColor;

  if (hasBg) {
    return <SolidColorCard merged={merged} almaText={almaText} onCheckout={onCheckout} accentColor={accentColor} />;
  }

  return <MaquetteCard merged={merged} almaText={almaText} onCheckout={onCheckout} accentColor={accentColor} />;
}

// ---------------------------------------------------------------------------
// Shared props
// ---------------------------------------------------------------------------

interface CardModeProps {
  merged: MergedPricingService;
  almaText: string;
  onCheckout?: () => void;
  accentColor: string;
}

// ---------------------------------------------------------------------------
// SOLID-COLOR MODE — full colored bg, centered white text
// ---------------------------------------------------------------------------

function SolidColorCard({ merged, almaText, onCheckout }: CardModeProps) {
  const hasContent = !!(
    merged.title || merged.priceLabel || merged.validity || merged.limitText ||
    (merged.ctaLabel && onCheckout)
  );

  return (
    <Box
      className="topBlockCard topBlockCard--solid"
      position="relative"
      borderRadius={cardBorderRadius}
      overflow="hidden"
      bg={merged.bgColor}
      display="flex"
      flexDirection="column"
      minH="320px"
      maxW="320px"
      w="full"
    >
      {merged.ribbon && (
        <Box
          className="topBlockRibbon"
          position="absolute"
          top={4}
          left={0}
          px={4}
          py={1.5}
          bg={merged.ribbon.color}
          color="white"
          fontSize="sm"
          fontWeight="700"
          textTransform="uppercase"
          letterSpacing="wider"
          borderRightRadius={cardBorderRadius}
          zIndex={2}
        >
          {merged.ribbon.text}
        </Box>
      )}

      {merged.image && merged.image.url && (
        <Image
          className="topBlockImage"
          src={merged.image.url}
          alt={merged.title || ''}
          w="full"
          h="full"
          objectFit="cover"
          position="absolute"
          inset={0}
        />
      )}

      {hasContent && (
        <Flex
          className="topBlockContent"
          direction="column"
          justify="center"
          align="center"
          flex="1"
          position="relative"
          zIndex={1}
          p={6}
          textAlign="center"
          gap={2}
        >
          {merged.title && (
            <Text className="serviceName" fontSize={{ base: 'xl', md: '2xl' }} fontWeight="800" textTransform="uppercase" letterSpacing="wide" color={merged.titleColor || 'white'} lineHeight="1.15">
              {merged.title}
            </Text>
          )}

          {merged.priceLabel && (
            <Text className="servicePrice" fontSize={{ base: '3xl', md: '4xl' }} color={merged.priceColor || 'white'}>
              {merged.priceLabel}
            </Text>
          )}

          {merged.pricePerSessionLabel && (
            <Text className="servicePricePerSession" fontSize="sm" fontWeight="600" textTransform="uppercase" color={merged.textColor || 'whiteAlpha.800'}>
              {merged.pricePerSessionLabel}
            </Text>
          )}

          {merged.validity && (
            <Text className="serviceExpiration" fontSize="sm" color={merged.textColor || 'whiteAlpha.900'}>
              {merged.validity}
            </Text>
          )}

          {merged.almaEnabled && (
            <AlmaMention variant="card" text={almaText} color={merged.textColor || 'whiteAlpha.800'} fontSize="xs" />
          )}

          {merged.limitText && (
            <Text className="serviceLimitText" fontSize="xs" color={merged.textColor || 'whiteAlpha.800'}>
              {merged.limitText}
            </Text>
          )}

          {merged.ctaLabel && onCheckout && (
            <Button className="serviceCardButton" size="md" borderRadius="full" onClick={onCheckout} mt={2} bg="white" color={merged.bgColor} _hover={{ bg: 'whiteAlpha.900' }} px={8} textTransform="uppercase" fontWeight="700" letterSpacing="wider">
              {merged.ctaLabel}
            </Button>
          )}
        </Flex>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// MAQUETTE MODE — pixel-perfect Figma match
//
// Layout:
//   ┌──────────────────────────────────┐
//   │  RIBBON BAR (full-width, accent) │  ← optional
//   ├──────────────────────────────────┤
//   │  padding 24px                    │
//   │                                  │
//   │  TITLE (bold, uppercase, black)  │
//   │                                  │
//   │  PRICE  (very large, bold)       │
//   │  per-session (small, uppercase)  │
//   │                                  │
//   │         — spacer —               │
//   │                                  │
//   │  [ACHETER]    Valable 14 jours*. │
//   │               Un seul achat …    │
//   └──────────────────────────────────┘
// ---------------------------------------------------------------------------

function MaquetteCard({ merged, almaText, onCheckout, accentColor }: CardModeProps) {
  const hasBottomInfo = !!(merged.validity || merged.limitText || merged.almaEnabled);
  const hasBottomRow = !!(hasBottomInfo || (merged.ctaLabel && onCheckout));
  const hasContent = !!(merged.title || merged.priceLabel || hasBottomRow);

  return (
    <Box
      className="topBlockCard topBlockCard--maquette"
      borderRadius={cardBorderRadius}
      overflow="hidden"
      bg="#f5f5f5"
      display="flex"
      flexDirection="column"
      minH="320px"
      maxW="320px"
      w="full"
    >
      {/* ── Ribbon bar — reserve space even when absent so titles align ── */}
      <Box
        className={merged.ribbon ? 'topBlockRibbon' : 'topBlockRibbonSpacer'}
        w="full"
        px={5}
        py={merged.ribbon ? 2.5 : 0}
        minH="40px"
        bg={merged.ribbon ? (merged.ribbon.color || accentColor) : 'transparent'}
        color="white"
        fontSize="sm"
        fontWeight="700"
        textTransform="uppercase"
        letterSpacing="wide"
        flexShrink={0}
        display="flex"
        alignItems="center"
      >
        {merged.ribbon?.text}
      </Box>

      {/* ── Image only (no text content) ── */}
      {merged.image && merged.image.url && !hasContent && (
        <Image className="topBlockImage" src={merged.image.url} alt="" w="full" flex="1" objectFit="cover" />
      )}

      {/* ── Image + text ── */}
      {merged.image && merged.image.url && hasContent && (
        <Image className="topBlockImage" src={merged.image.url} alt="" w="full" h="140px" objectFit="cover" flexShrink={0} />
      )}

      {/* ── Text content ── */}
      {hasContent && (
        <Flex className="topBlockContent" direction="column" flex="1" px={6} pt={5} pb={5}>
          {merged.title && (
            <Text className="serviceName" fontSize={{ base: '22px', md: '26px' }} fontWeight="900" textTransform="uppercase" lineHeight="1.1" color={merged.titleColor || '#1a1a1a'}>
              {merged.title}
            </Text>
          )}

          {merged.priceLabel && (
            <Box className="servicePriceBlock" mt={4}>
              <Text className="servicePrice" fontSize={{ base: '32px', md: '38px' }} color={merged.priceColor || '#1a1a1a'} lineHeight="1">
                {merged.priceLabel}
              </Text>
              {merged.pricePerSessionLabel && (
                <Text className="servicePricePerSession" fontSize="14px" fontWeight="400" textTransform="uppercase" color={merged.textColor || '#1a1a1a'} mt={1}>
                  {merged.pricePerSessionLabel}
                </Text>
              )}
            </Box>
          )}

          <Box flex="1" minH={4} />

          {hasBottomRow && (
            <Flex className="topBlockBottomRow" align="flex-end" justify="space-between" gap={4}>
              {merged.ctaLabel && onCheckout ? (
                <Button className="serviceCardButton" borderRadius="full" onClick={onCheckout} bg={accentColor} color="white" _hover={{ opacity: 0.9 }} px={7} h="42px" fontSize="14px" fontWeight="700" textTransform="uppercase" letterSpacing="wide" flexShrink={0}>
                  {merged.ctaLabel}
                </Button>
              ) : (
                <Box />
              )}

              {(hasBottomInfo || merged.almaEnabled) && (
                <Box className="topBlockBottomInfo" textAlign="right">
                  {merged.validity && (
                    <Text className="serviceExpiration" fontSize="13px" color={merged.textColor || '#4a4a4a'} lineHeight="1.4">
                      {merged.validity}
                    </Text>
                  )}
                  {merged.almaEnabled && (
                    <AlmaMention variant="card" text={almaText} color={merged.textColor || '#4a4a4a'} fontSize="13px" />
                  )}
                  {merged.limitText && (
                    <Text className="serviceLimitText" fontSize="11px" color={merged.textColor || '#4a4a4a'} lineHeight="1.4">
                      {merged.limitText}
                    </Text>
                  )}
                </Box>
              )}
            </Flex>
          )}
        </Flex>
      )}
    </Box>
  );
}
